import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronRight,
  Info,
  Shield,
  Settings,
  AlertCircle,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Smooth scroll to anchor links
const scrollToAnchor = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const TableOfContents = ({ t }) => (
  <div className="sticky top-24 bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-8">
    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
      <span className="mr-2">📋</span> {t('cookiePolicy.tocTitle')}
    </h2>
    <nav>
      <ul className="space-y-2">
        {[
          { id: 'introduccion', label: t('cookiePolicy.toc.introduccion') },
          { id: 'definicion', label: t('cookiePolicy.toc.definicion') },
          { id: 'tipos', label: t('cookiePolicy.toc.tipos') },
          { id: 'finalidad', label: t('cookiePolicy.toc.finalidad') },
          { id: 'gestion', label: t('cookiePolicy.toc.gestion') },
          { id: 'derechos', label: t('cookiePolicy.toc.derechos') },
          { id: 'cambios', label: t('cookiePolicy.toc.cambios') },
          { id: 'contacto', label: t('cookiePolicy.toc.contacto') },
        ].map((item) => (
          <li key={item.id}>
            <button
              onClick={() => scrollToAnchor(item.id)}
              className="text-blue-600 hover:text-blue-800 text-sm flex items-center group w-full text-left"
            >
              <ChevronRight className="h-3 w-3 mr-2 text-blue-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  </div>
);

const Section = ({ id, title, children, icon: Icon = Info, className = '' }) => (
  <section id={id} className={`mb-10 scroll-mt-20 ${className}`}>
    <h2 className="text-2xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center">
      <Icon className="h-6 w-6 mr-2 text-blue-600" />
      {title}
    </h2>
    <div className="prose prose-blue max-w-none">
      {children}
    </div>
  </section>
);

const CookiePolicy = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // Scroll to anchor if hash exists in URL
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => scrollToAnchor(id), 100);
    }
  }, [location]);

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Header */}
      <header className="relative overflow-hidden text-white shadow-lg">
        {/* Fondo con patrón */}
        <div className="absolute inset-0 hero-pattern backdrop-blur-sm" />
        {/* Capa semitransparente para mejorar contraste */}
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <Link
                to="/"
                className="inline-flex items-center text-blue-100 hover:text-white mb-4 md:mb-0 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-1" />
                {t('cookiePolicy.backHome')}
              </Link>
              <h1 className="text-3xl md:text-4xl font-bold mt-2">{t('cookiePolicy.title')}</h1>
              <p className="text-blue-100 mt-2">
                {t('cookiePolicy.lastUpdate')}
              </p>
            </div>
            <div className="mt-6 md:mt-0">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-700 bg-opacity-50 text-sm font-medium">
                <Shield className="h-4 w-4 mr-2" /> {t('cookiePolicy.documentInfo')}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-1/4">
            <div className="sticky top-24 space-y-6">
              <TableOfContents t={t} />

              <div className="hidden lg:block bg-blue-50 p-6 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-800 mb-3 flex items-center">
                  <Info className="h-5 w-5 mr-2" /> {t('cookiePolicy.helpTitle')}
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  {t('cookiePolicy.helpDescription')}
                </p>
                <Link
                  to="/contacto"
                  className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  {t('cookiePolicy.contactLink')} <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
              <Section id="introduccion" title={t('cookiePolicy.sections.introduccion.title')} icon={Info}>
                <p className="mb-4">
                  {t('cookiePolicy.sections.introduccion.paragraph1', { defaultValue: 'En PLASA, valoramos su privacidad...' })}
                </p>
                <p className="mb-4">
                  {t('cookiePolicy.sections.introduccion.paragraph2')}
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-4">
                  <p className="text-blue-700 text-sm">
                    <span className="font-medium">{t('cookiePolicy.sections.introduccion.note').split(':')[0]}:</span> {t('cookiePolicy.sections.introduccion.note').split(':')[1]}
                  </p>
                </div>
              </Section>

              <Section id="definicion" title={t('cookiePolicy.sections.definicion.title')} icon={Info}>
                <p className="mb-4">
                  {t('cookiePolicy.sections.definicion.paragraph1')}
                </p>
                <p className="mb-4">
                  {t('cookiePolicy.sections.definicion.paragraph2')}
                </p>
              </Section>

              <Section id="tipos" title={t('cookiePolicy.sections.tipos.title')} icon={Info}>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('cookiePolicy.sections.tipos.subsections.estrictas.title')}</h3>
                  <p className="text-gray-600 mb-2">
                    {t('cookiePolicy.sections.tipos.subsections.estrictas.paragraph')}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">{t('cookiePolicy.sections.tipos.subsections.estrictas.examples').split(':')[0]}:</span>
                    {t('cookiePolicy.sections.tipos.subsections.estrictas.examples').split(':')[1]}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('cookiePolicy.sections.tipos.subsections.analisis.title')}</h3>
                  <p className="text-gray-600 mb-2">
                    {t('cookiePolicy.sections.tipos.subsections.analisis.paragraph')}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">{t('cookiePolicy.sections.tipos.subsections.analisis.examples').split(':')[0]}:</span>
                    {t('cookiePolicy.sections.tipos.subsections.analisis.examples').split(':')[1]}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('cookiePolicy.sections.tipos.subsections.funcionalidad.title')}</h3>
                  <p className="text-gray-600 mb-2">
                    {t('cookiePolicy.sections.tipos.subsections.funcionalidad.paragraph')}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">{t('cookiePolicy.sections.tipos.subsections.funcionalidad.examples').split(':')[0]}:</span>
                    {t('cookiePolicy.sections.tipos.subsections.funcionalidad.examples').split(':')[1]}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('cookiePolicy.sections.tipos.subsections.publicidad.title')}</h3>
                  <p className="text-gray-600 mb-2">
                    {t('cookiePolicy.sections.tipos.subsections.publicidad.paragraph')}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">{t('cookiePolicy.sections.tipos.subsections.publicidad.examples').split(':')[0]}:</span>
                    {t('cookiePolicy.sections.tipos.subsections.publicidad.examples').split(':')[1]}
                  </p>
                </div>
              </Section>

              <Section id="finalidad" title={t('cookiePolicy.sections.finalidad.title')} icon={Info}>
                <p className="mb-4">
                  {t('cookiePolicy.sections.finalidad.paragraph1')}
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-2 mb-6">
                  <li>{t('cookiePolicy.sections.finalidad.list.item1')}</li>
                  <li>{t('cookiePolicy.sections.finalidad.list.item2')}</li>
                  <li>{t('cookiePolicy.sections.finalidad.list.item3')}</li>
                  <li>{t('cookiePolicy.sections.finalidad.list.item4')}</li>
                  <li>{t('cookiePolicy.sections.finalidad.list.item5')}</li>
                </ul>
              </Section>

              <Section id="gestion" title={t('cookiePolicy.sections.gestion.title')} icon={Settings}>
                <p className="mb-4">
                  {t('cookiePolicy.sections.gestion.paragraph1')}
                </p>

                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('cookiePolicy.sections.gestion.subsections.navegador.title')}</h3>
                <p className="mb-4">
                  {t('cookiePolicy.sections.gestion.subsections.navegador.paragraph')}
                </p>

                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('cookiePolicy.sections.gestion.subsections.terceros.title')}</h3>
                <p className="mb-4">
                  {t('cookiePolicy.sections.gestion.subsections.terceros.paragraph')}
                </p>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
                  <p className="text-yellow-700">
                    <span className="font-medium">{t('cookiePolicy.sections.gestion.note').split(':')[0]}:</span> {t('cookiePolicy.sections.gestion.note').split(':')[1]}
                  </p>
                </div>
              </Section>

              <Section id="derechos" title={t('cookiePolicy.sections.derechos.title')} icon={Shield}>
                <p className="mb-4">
                  {t('cookiePolicy.sections.derechos.paragraph1')}
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">{t('cookiePolicy.sections.derechos.list.acceso.title')}</h4>
                    <p className="text-gray-600 text-sm">
                      {t('cookiePolicy.sections.derechos.list.acceso.description')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">{t('cookiePolicy.sections.derechos.list.rectificacion.title')}</h4>
                    <p className="text-gray-600 text-sm">
                      {t('cookiePolicy.sections.derechos.list.rectificacion.description')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">{t('cookiePolicy.sections.derechos.list.cancelacion.title')}</h4>
                    <p className="text-gray-600 text-sm">
                      {t('cookiePolicy.sections.derechos.list.cancelacion.description')}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">{t('cookiePolicy.sections.derechos.list.oposicion.title')}</h4>
                    <p className="text-gray-600 text-sm">
                      {t('cookiePolicy.sections.derechos.list.oposicion.description')}
                    </p>
                  </div>
                </div>
              </Section>

              <Section id="cambios" title={t('cookiePolicy.sections.cambios.title')} icon={AlertCircle}>
                <p className="mb-4">
                  {t('cookiePolicy.sections.cambios.paragraph1')}
                </p>
                <p>
                  {t('cookiePolicy.sections.cambios.paragraph2')}
                </p>
              </Section>

              <Section id="contacto" title={t('cookiePolicy.sections.contacto.title')} icon={Mail}>
                <p className="mb-4">
                  {t('cookiePolicy.sections.contacto.paragraph1')}
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('cookiePolicy.sections.contacto.info.title')}</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <Mail className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('cookiePolicy.sections.contacto.info.email')}</span>
                      </li>
                      <li className="flex items-start">
                        <Phone className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('cookiePolicy.sections.contacto.info.phone')}</span>
                      </li>
                      <li className="flex items-start">
                        <MapPin className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('cookiePolicy.sections.contacto.info.address')}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('cookiePolicy.sections.contacto.hours.title')}</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex justify-between">
                        <span>{t('cookiePolicy.sections.contacto.hours.labelMonFri')}</span>
                        <span className="font-medium">{t('cookiePolicy.sections.contacto.hours.monFri')}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>{t('cookiePolicy.sections.contacto.hours.labelSat')}</span>
                        <span className="font-medium">{t('cookiePolicy.sections.contacto.hours.sat')}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>{t('cookiePolicy.sections.contacto.hours.labelSun')}</span>
                        <span className="font-medium">{t('cookiePolicy.sections.contacto.hours.sun')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Section>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-400">
            <p>{t('cookiePolicy.footer.copy')}</p>
            <div className="mt-2">
              <Link to="/aviso-de-privacidad" className="hover:text-white transition-colors">
                {t('cookiePolicy.footer.privacyLink')}
              </Link>
              <span className="mx-2">•</span>
              <Link to="/terminos-y-condiciones" className="hover:text-white transition-colors">
                {t('cookiePolicy.footer.termsLink')}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

export default CookiePolicy;
